import { createApp } from 'vue'
import { cloneDeep } from 'lodash'
import swal from 'sweetalert'

window.pawine = window.pawine || {}

const app = createApp({
  data() {
    return {
      events: window.pawine.events || [],
      filteredEvents: [],
      isGeolocating: false,
      searchLocation: {
        postalCode: null,
        latitude: null,
        longitude: null,
      },
      filterSelections: {
        keyword: null,
        postalCode: null,
        category: null,
        location: null,
      },
      searchForm: {
        keyword: null,
        postalCode: null,
        radius: 50,
      }
    }
  },
  mounted() {
    this.filteredEvents = this.events
  },
  computed: {
    /**
     * Group events by year/month.
     */
    eventMonths() {
      let dates = this.filteredEvents.reduce((accumulator, currentValue) => {
        const eventDate = new Date(currentValue.date)
        const monthString = eventDate.getFullYear() + '-' + (((parseInt(eventDate.getMonth()) + 1).toString()).padStart(2, '0'))

        accumulator[ monthString ] = accumulator[ monthString ] || {}

        accumulator[ monthString ].title = new Intl.DateTimeFormat('en-US', {month: 'long', year: 'numeric'}).format(eventDate)

        accumulator[ monthString ].events = accumulator[ monthString ].events || []
        accumulator[ monthString ].events.push(currentValue)

        return accumulator
      }, {})

      // Sort groups in ascending order.
      let groupedDates = Object.keys(dates).sort().reduce((result, key) => {
        result[key] = dates[key]
        return result
      }, {})

      return groupedDates
    },
    eventCategories() {
      // Unique categories
      return this.events.map(event => event?.category?.name).sort().filter((value, index, array) => {
        return array.indexOf(value) === index
      })
    },
    eventLocations() {
      // All locations
      let locations = this.events.map(event => {

        // If a category is selected, then only show the available locations.
        if (
          this.filterSelections.category &&
          event.category &&
          event.category.name && 
          event.category.name != this.filterSelections.category
        ) {
          return null
        }

        return event.venueCity
      })

      // Unique locations
      return locations.sort().filter((value, index, array) => {
        return array.indexOf(value) === index
      })
    },
    totalFilteredEvents() {
      return this.filteredEvents.length
    }
  },
  watch: {
    // Clear the location filter when the category filter is changed.
    'filterSelections.category'() {
      this.filterSelections.location = null
    }
  },
  methods: {
    search() {
      this.filterSelections['keyword'] = this.searchForm.keyword
      this.filterSelections['postalCode'] = this.searchForm.postalCode

      this.filteredEvents = this.filterEvents()
    },
    setFilter(filter, selection) {
      if (this.filterSelections[filter] == selection) {
        this.filterSelections[filter] = null
      } else {
        this.filterSelections[filter] = selection
      }

      this.filteredEvents = this.filterEvents()
    },
    filterEvents() {
      let filtered = cloneDeep(this.events)

      // Keyword search
      if (this.filterSelections.keyword) {
        filtered = filtered.filter(event => {
          if (
            (event.title.toLowerCase().indexOf(this.filterSelections.keyword) > -1) || 
            (event.shortDescription.toLowerCase().indexOf(this.filterSelections.keyword) > -1)
          ) {
            return true
          }

          return false
        })
      }

      // Postal code search
      if (this.filterSelections.postalCode) {

        if (
          this.filterSelections.postalCode != this.searchLocation.postalCode || (
            ! this.searchLocation.latitude || 
            ! this.searchLocation.longitude
          )
        ) {
          this.getPostalCodeCoords(this.filterSelections.postalCode)
        }

        filtered = filtered.filter(event => {

          if (! event.venueLatitude || ! event.venueLongitude) {
            return false
          }

          // Calculate the distance, in miles, between the provided postal code 
          // and the event's postal code.
          const distance = 
            this.radiansToDegrees(Math.acos(
              Math.sin(this.degreesToRadians(event.venueLatitude)) *
              Math.sin(this.degreesToRadians(this.searchLocation.latitude))
              + Math.cos(this.degreesToRadians(event.venueLatitude))
              * Math.cos(this.degreesToRadians(this.searchLocation.latitude))
              * Math.cos(this.degreesToRadians(event.venueLongitude - this.searchLocation.longitude))
            )) * 69.06

          // console.log(distance)

          return distance <= this.searchForm.radius
        })
      }

      // Filters
      filtered = filtered.filter(event => {

        // Category
        if (this.filterSelections.category) {
          if (! event.category || event.category.name != this.filterSelections.category) {
            return false
          }
        }

        // Venue City
        if (this.filterSelections.location) {
          if (event.venueCity != this.filterSelections.location) {
            return false
          }
        }

        return true
      })

      return filtered
    },
    async getPostalCodeCoords(postalCode) {
      this.isGeolocating = true
      this.searchLocation.postalCode = null
      this.searchLocation.latitude = null
      this.searchLocation.longitude = null

      try {

        // Get the latitude/longitude coordinates of the provided postal code
        // from geocod.io
        // @see https://www.geocod.io/docs/?shell#single-address
        const resp = await fetch('https://api.geocod.io/v1.7/geocode?' + new URLSearchParams({
          api_key: process.env.MIX_GEOCODIO_APY_KEY,
          q: postalCode,
          limit: 1,
        }))

        const json = await resp.json()

        this.searchLocation.postalCode = postalCode
        this.searchLocation.latitude = json.results[0].location.lat
        this.searchLocation.longitude = json.results[0].location.lng
        this.isGeolocating = false

        this.filteredEvents = this.filterEvents()

      } catch (error) {
        console.log(error)

        this.isGeolocating = false

        swal('', 'An unknown error occurred.')
      }
    },
    degreesToRadians(degrees) {
      return degrees * (Math.PI / 180)
    },
    radiansToDegrees(radians) {
      return radians * (180 / Math.PI)
    },
  }
})

app.mount('#event-listing')
